import axios from 'axios';
import { API_URL } from '../config';
import { AxiosRequestConfig } from 'axios';

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  permission?: string;
}


export const createRimborsoSpese = async (token: string, data: any) => {
  const type = data.get('type');
  
  let endpoint = '/cost-refunds/';
  if (type === 'mileage') {
    endpoint = '/v2/mileage-refunds/';
  }

  const response = await axios.post(`${API_URL}${endpoint}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'Authorization': `${token}`,
    }
  });

  return response.data;
};

export const listaRichiesteRimborsoSpeseCompany = async (
  token: string, 
  page: number, 
  status: string, 
  pageSize: number | null, 
  month: number,  
  year: number,
  permission: string | null,
  employeeIds: number[] = [], 
) => {
  const baseParams = {
    page,
    page_size: pageSize,
    status,
    month,
    year,
    employees: employeeIds.join(','),
  };

  const headers = {
    Authorization: token,
  };

  const config = {
    headers,
    params: baseParams,
    permission,
  } as CustomAxiosRequestConfig;

  const [costResponse, mileageResponse] = await Promise.all([
    axios.get(`${API_URL}/cost-refunds/`, config),
    axios.get(`${API_URL}/v2/mileage-refunds/`, config),
  ]);

  const mergedResults = [
    ...costResponse.data.results.map((r: any) => ({ ...r, type: r.type || "regular" })),
    ...mileageResponse.data.results.map((r: any) => ({ ...r, type: r.type || "mileage" })),
  ];

  // oordina per data desc per avere ordine stabile già da qui
  mergedResults.sort((a: any, b: any) => {
    const da = new Date(a.date || a.created_at || 0).getTime();
    const db = new Date(b.date || b.created_at || 0).getTime();
    return db - da;
  });

  return {
    // count totale (attenzione: count sono totali per-endpoint, OK sommarli solo come "info")
    count: Number(costResponse.data.count || 0) + Number(mileageResponse.data.count || 0),

    // PAGINA corrente (cost page + mileage page)
    results: mergedResults,

    // fondamentali per iterare tutte le pagine
    next_cost: costResponse.data.next,
    next_mileage: mileageResponse.data.next,
  };
};



export const processRichiestaRimborso = async (
  token: string,
  id: number,
  status: string,
  type: 'regular' | 'mileage'
) => {
  const formData = new FormData();
  formData.append('status', status);

  const endpoint = type === 'mileage' ? 'v2/mileage-refunds' : 'cost-refunds';

  const response = await axios.patch(`${API_URL}/${endpoint}/${id}/`, 
    formData, 
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `${token}`,
      },
    }
  );
  return response.data;
};


export const updateRatePerKm = async (token: string, id: number, rate_per_km: number | null) => {
  const response = await axios.patch(
    `${API_URL}/v2/mileage-refunds/${id}/`,
    { rate_per_km },
    {
      headers: {
        Authorization: `${token}`,
        'Content-Type': 'application/json',
      },
    }
  );
  return response.data;
};


export const processAllRefundRequests = async (token: string, status: string, year: number, month: number, employeeId?: number
) => {
  let url = `${API_URL}/cost-refunds/${status}-all/?year=${year}&month=${month}`;
  
  if (employeeId) {
    url += `&employee=${employeeId}`;
  }

  const response = await axios.post(
    url,
    {},
    {
      headers: {
        Authorization: `${token}`,
      }
    }
  );

  return response.data;
};

export const processAllMileageRefundRequests = async (
  token: string,
  status: string,
  year: number,
  month: number,
  employeeId?: number
) => {
  let url = `${API_URL}/mileage-refunds/${status}-all/?year=${year}&month=${month}`;

  if (employeeId) {
    url += `&employee=${employeeId}`;
  }

  const response = await axios.post(
    url,
    {},
    {
      headers: {
        Authorization: `${token}`,
      }
    }
  );

  return response.data;
};

export const patchRichiestaRimborso = async (
  token: string,
  id: number,
  updatedData: any,
  type: 'regular' | 'mileage' = 'regular'
) => {
  const endpoint =
    type === 'mileage'
      ? `${API_URL}/v2/mileage-refunds/${id}/`
      : `${API_URL}/cost-refunds/${id}/`;

  // Se arriva già un FormData dal form (come nel nuovo edit mileage) lo uso direttamente
  if (typeof FormData !== 'undefined' && updatedData instanceof FormData) {
    const response = await axios.patch(endpoint, updatedData, {
      headers: {
        Authorization: `${token}`,
      },
    });
    return response.data;
  }

  const fd = new FormData();

  const appendIfValue = (key: string, value: any) => {
    if (value !== undefined && value !== null && value !== '') {
      fd.append(key, value);
    }
  };

  appendIfValue('title', updatedData.title);
  appendIfValue('description', updatedData.description);
  appendIfValue('date', updatedData.date);

  if (type === 'regular') {
    appendIfValue('amount', updatedData.amount);
    // file (upload/rimozione)
    if ('file_binary' in updatedData) {
      // può essere File o "" per rimuovere
      fd.append('file_binary', updatedData.file_binary);
    } else if ('file' in updatedData) {
      // compat vecchia: accetto 'file' come alias
      fd.append('file_binary', updatedData.file);
    }
  } else {
    // ----- MILEAGE (nuova struttura) -----
    // solo i campi effettivamente previsti dal BE
    appendIfValue('departure_location', updatedData.departure_location);
    appendIfValue('arrival_location', updatedData.arrival_location);
    appendIfValue('distance_km', updatedData.distance_km);

    // vehicle: SOLO il PK
    appendIfValue('vehicle', updatedData.vehicle);

    // rate_per_km: speciale — devo poter inviare "" per resettare a null
    if ('rate_per_km' in updatedData) {
      // può essere numero (string) o "" (reset)
      fd.append('rate_per_km', updatedData.rate_per_km);
    }

    // file (upload/rimozione)
    if ('file_binary' in updatedData) {
      fd.append('file_binary', updatedData.file_binary);
    } else if ('file' in updatedData) {
      fd.append('file_binary', updatedData.file);
    }

    appendIfValue('type', updatedData.type);
  }

  const response = await axios.patch(endpoint, fd, {
    headers: {
      Authorization: `${token}`,
    },
  });

  return response.data;
};

export const deleteRichiestaRimborso = async ( token: string, id: string | number,  refundKind: 'mileage' | 'regular') => {
  const endpoint =
    refundKind === 'mileage'
      ? `${API_URL}/v2/mileage-refunds/${id}/`
      : `${API_URL}/cost-refunds/${id}/`;

  const response = await axios.delete(endpoint, {
    headers: {
      Authorization: `${token}`,
    },
  });

  return response.data;
};


export const summaryRefundRequests = async (
  token: string,
  month: number,
  year: number,
  kind: 'all' | 'regular' | 'mileage' = 'all',
  pendingPage = 1,
  handledPage = 1,
  pageSize = 10,
  permission: string | null = null,
  employeeId?: number,
  status: 'all' | 'pending' | 'handled' = 'all',
  signal: AbortSignal | null = null,
) => {
  const config: any = {};
  if (signal) config.signal = signal;
  if (permission) config.permission = permission;

  const params: Record<string, any> = {
    month,
    year,
    kind,
    pending_page: pendingPage,
    handled_page: handledPage,
    page_size: pageSize,
    status,
  };

  if (employeeId != null) {
    params.employee_id = employeeId;
  }

  const response = await axios.get(`${API_URL}/refund-requests/summary/`, {
    headers: { Authorization: `${token}` },
    params,
    ...config,
  });

  return response.data;
};